import {
    Modal,
    Box,
    TextField,
    Button,
    FormLabel,
    CircularProgress,
} from "@mui/material";
import { useState, useEffect } from "react";
import { ModalBaseStyles, ModalHeader } from "../../../../Common/styles/modal";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { DateTime } from "luxon";
import { Goal, UpdateGoalData, goalsApi } from "../../../../../utils/goalsApi";
import { errorToastMessage, toastMessage } from "../../../../../utils/toast";

interface EditGoalDialogProps {
    open: boolean;
    onClose: () => void;
    goal: Goal | null;
    onSuccess: () => void;
}

const EditGoalDialog = ({ open, onClose, goal, onSuccess }: EditGoalDialogProps) => {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [confidenceScoreInput, setConfidenceScoreInput] = useState("");
    const [reminderTimes, setReminderTimes] = useState<DateTime[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (goal) {
            setTitle(goal.title || "");
            setDescription(goal.description || "");
            setConfidenceScoreInput(
                goal.confidenceScore != null && goal.confidenceScore !== undefined
                    ? String(goal.confidenceScore)
                    : "",
            );
            setReminderTimes(
                goal.reminderTimes
                    ? goal.reminderTimes.map((time) => {
                        const [hours, minutes] = time.split(":").map(Number);
                        return DateTime.now().set({ hour: hours, minute: minutes || 0, second: 0, millisecond: 0 });
                    })
                    : []
            );
        }
    }, [goal]);

    const handleAddReminderTime = () => {
        const now = DateTime.now();
        const timeWithZeroMinutes = now.set({ minute: 0, second: 0, millisecond: 0 });
        setReminderTimes([...reminderTimes, timeWithZeroMinutes]);
    };

    const handleRemoveReminderTime = (index: number) => {
        setReminderTimes(reminderTimes.filter((_, i) => i !== index));
    };

    const handleUpdateReminderTime = (index: number, time: DateTime | null) => {
        if (time) {
            const newTimes = [...reminderTimes];
            newTimes[index] = time.set({ minute: 0, second: 0, millisecond: 0 });
            setReminderTimes(newTimes);
        }
    };

    const handleSubmit = async () => {
        if (!goal || !title.trim()) {
            errorToastMessage(new Error("Title is required"));
            return;
        }

        try {
            setLoading(true);
            const trimmedConfidence = confidenceScoreInput.trim();
            let confidenceScore: number | null | undefined;
            if (trimmedConfidence === "") {
                confidenceScore = null;
            } else {
                const n = Number(trimmedConfidence);
                if (!Number.isInteger(n) || n < 0 || n > 100) {
                    errorToastMessage(
                        new Error("Confidence must be a whole number from 0 to 100, or left blank."),
                    );
                    setLoading(false);
                    return;
                }
                confidenceScore = n;
            }

            const updateData: UpdateGoalData = {
                title: title.trim(),
                description: description.trim() || "",
                reminderTimes: reminderTimes.map((time) => time.toFormat("HH:mm")),
                confidenceScore,
            };

            await goalsApi.updateGoal(goal.id, updateData);
            toastMessage("success", "Goal updated successfully");
            onSuccess();
            onClose();
        } catch (error) {
            errorToastMessage(error as Error);
        } finally {
            setLoading(false);
        }
    };

    if (!goal) return null;

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{ ...ModalBaseStyles, width: "500px", minHeight: "auto" }}>
                <ModalHeader title="Edit Goal" onCloseClick={onClose} />

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {/* Title */}
                    <TextField
                        label="Title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        fullWidth
                        required
                        size="small"
                    />

                    {/* Description */}
                    <TextField
                        label="Description (Optional)"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        minRows={3}
                        fullWidth
                        size="small"
                    />

                    <TextField
                        label="Confidence score (optional)"
                        value={confidenceScoreInput}
                        onChange={(e) => setConfidenceScoreInput(e.target.value)}
                        type="number"
                        inputProps={{ min: 0, max: 100, step: 1 }}
                        fullWidth
                        size="small"
                        helperText="Whole number from 0 to 100."
                    />

                    {/* Reminder Times */}
                    <Box>
                        <FormLabel sx={{ fontSize: "0.875rem", mb: 1, display: "block" }}>
                            Reminder Times (Optional)
                        </FormLabel>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {reminderTimes.map((time, index) => (
                                <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                    <TimePicker
                                        label="Time"
                                        value={time}
                                        onChange={(newValue: DateTime | null) => handleUpdateReminderTime(index, newValue)}
                                        views={['hours']}
                                        renderInput={(params: any) => (
                                            <TextField {...params} size="small" sx={{ width: 150 }} />
                                        )}
                                    />
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        onClick={() => handleRemoveReminderTime(index)}
                                    >
                                        Remove
                                    </Button>
                                </Box>
                            ))}
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleAddReminderTime}
                                sx={{ width: 180 }}
                            >
                                Add Reminder Time
                            </Button>
                        </Box>
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
                        <Button variant="outlined" onClick={onClose} disabled={loading}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={loading || !title.trim()}
                        >
                            {loading ? <CircularProgress size={20} /> : "Update Goal"}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
};

export default EditGoalDialog;
